<template>
  <div>
    <e-filters
      :searching="fetching"
      @search="filterData"
      @reset="resetFiltersLocal"
    >
      <b-row>
        <b-col md="2">
          <FormulateInput
            id="counting-id"
            v-model="filters.countingId"
            name="countingId"
            :label="$t('Id da contagem')"
            type="text"
          />
        </b-col>

        <b-col md="4">
          <e-store-combo
            v-model="filters.storeId"
            :required="false"
            :placeholder="$t('Todas')"
            :only-active-options="true"
          />
        </b-col>

        <b-col md="2">
          <FormulateInput
            id="status"
            v-model="filters.status"
            type="vue-select"
            :label="$t('Status')"
            :placeholder="$t('Todos')"
            :options="stockCountingStatusOptions()"
          />
        </b-col>

        <b-col md="4">
          <FormulateInput
            id="period"
            v-model="filters.period"
            type="date-range-picker"
            :label="$t('Período')"
            :time-picker="false"
          />
        </b-col>

        <b-col md="3">
          <FormulateInput
            id="group-id"
            v-model="filters.groupId"
            type="vue-select"
            :label="$t('Grupo')"
            :placeholder="$t('Todos')"
            :options="getComboGroups"
          />
        </b-col>

        <b-col md="3">
          <FormulateInput
            id="brand-id"
            v-model="filters.brandId"
            type="vue-select"
            :label="$t('Marca')"
            :placeholder="$t('Todas')"
            :options="getComboBrands"
          />
        </b-col>
      </b-row>
    </e-filters>

    <b-card-actions
      action-refresh
      :show-loading="fetching"
      @refresh="filterData"
    >
      <div slot="title">
        <e-page-size-selector
          :per-page="paging.pageSize"
          @change="pageSizeChange"
        />
      </div>

      <b-table
        ref="table-stock-countings"
        responsive
        show-empty
        striped
        class="mb-1 bordered"
        :empty-text="getEmptyTableMessage($tc('STOCK_COUNTING.NAME'), 'female', queryExecuted)"
        :fields="fields"
        :items="stockCountings"
        no-local-sorting
        :sort-by.sync="sorting.sortBy"
        :sort-desc.sync="sorting.sortDesc"
        @sort-changed="getData"
      >
        <template #cell(action)="row">
          <e-grid-actions
            :show-update="showUpdateAction(row.item)"
            :show-delete="false"
            :show-read-only="$can('Read', 'StockCounting')"
            @update="showEdit(row.item)"
            @read-only="showReadOnly(row.item)"
          />
        </template>

        <template #cell(status)="{ item }">
          <e-status-badge
            :status="item.status"
            :options="stockCountingStatusOptions()"
          />
        </template>
      </b-table>

      <b-row>
        <b-col cols="6">
          <e-pagination-summary
            :current-page="paging.currentPage"
            :per-page="paging.pageSize"
            :total="paging.rowCount"
            :total-on-page="paging.rowsInCurrentPage"
          />
        </b-col>
        <b-col cols="6">
          <b-pagination
            v-model="paging.currentPage"
            align="right"
            :total-rows="paging.rowCount"
            :per-page="paging.pageSize"
            aria-controls="table-stock-countings"
            @change="pageChange"
          />
        </b-col>
      </b-row>
    </b-card-actions>

    <stock-counting-sidebar
      ref="sidebar"
      @save="showEdit"
    />

    <fab
      v-if="$can('Create', 'StockCounting')"
      :main-tooltip="$t('Adicionar Contagem')"
      @click="showCreate"
    />
  </div>
</template>

<script>
import { BRow, BCol, BPagination, BTable } from 'bootstrap-vue'
import BCardActions from '@core/components/b-card-actions/BCardActions.vue'
import {
  EPageSizeSelector,
  EPaginationSummary,
  EFilters,
  EStoreCombo,
  EGridActions,
  EStatusBadge,
} from '@/views/components'
import fab from '@/views/components/FAB.vue'
import { formulateValidation, productDomains, stockDomain } from '@/mixins'
import { mapActions, mapGetters, mapState } from 'vuex'
import StockCountingSidebar from './components/StockCountingSidebar.vue'

export default {
  components: {
    BRow,
    BCol,
    BCardActions,
    BPagination,
    BTable,
    EPaginationSummary,
    EPageSizeSelector,
    fab,
    EFilters,
    EStoreCombo,
    StockCountingSidebar,
    EGridActions,
    EStatusBadge,
  },

  mixins: [formulateValidation, stockDomain, productDomains],

  data() {
    return {
      queryExecuted: false,
      fetching: false,
    }
  },

  computed: {
    ...mapGetters('app', ['storeOptions']),
    ...mapGetters('pages/catalog/products', ['getComboGroups', 'getComboBrands']),
    ...mapState('pages/stock/stockCounting', {
      stockCountings: 'stockCountings',
      paging: 'paging',
      sorting: 'sorting',
      filters: 'filters',
    }),

    showUpdateAction() {
      return item =>
        this.$can('Update', 'StockCounting') &&
        item.status === this.stockCountingStatusEnum.IN_PROGRESS
    },

    fields() {
      return [
        {
          label: this.$t('Ações'),
          key: 'action',
          class: 'text-center',
          thStyle: { width: '100px' },
        },
        {
          label: this.$t('Id'),
          key: 'id',
          class: 'text-center',
          thStyle: { width: '80px' },
          sortable: true,
        },
        {
          label: this.$t('Data/Hora'),
          key: 'createdDate',
          class: 'text-center',
          thStyle: { width: '140px' },
          formatter: value => this.$options.filters.datetime(value),
          sortable: true,
        },
        {
          label: this.$t('Status'),
          key: 'status',
          class: 'text-center',
          thStyle: { width: '120px' },
          sortable: true,
        },
        {
          label: this.$t('Loja'),
          key: 'store.id',
          formatter: (val, index, item) => this.$options.filters.storeName(item.store),
          sortable: true,
          thStyle: { minWidth: '150px' },
        },
        {
          label: this.$t('Grupo'),
          key: 'group.name',
          formatter: (val, index, item) => item.group?.name || '-',
          thStyle: { minWidth: '120px' },
        },
        {
          label: this.$t('Marca'),
          key: 'brand.name',
          formatter: (val, index, item) => item.brand?.name || '-',
          thStyle: { minWidth: '120px' },
        },
        {
          label: this.$t('Descrição'),
          key: 'description',
          formatter: value => value || '-',
          thStyle: { minWidth: '180px' },
        },
        {
          label: this.$t('Qtd. SKUs'),
          key: 'skusCount',
          class: 'text-center',
          thStyle: { width: '90px' },
          formatter: value => this.$options.filters.number(value, 0),
          sortable: true,
        },
        {
          label: this.$t('Perc. Completado'),
          key: 'percentCompleted',
          class: 'text-center',
          thStyle: { width: '120px' },
          formatter: value => this.$options.filters.percentage(value),
          sortable: true,
        },
        {
          label: this.$t('Criado por'),
          key: 'createdBy',
          formatter: value => value || '-',
          thStyle: { minWidth: '130px' },
        },
      ]
    },
  },

  async mounted() {
    try {
      this.fetching = true
      await this.fetchProductCombos()
    } finally {
      this.fetching = false
    }
  },

  methods: {
    ...mapActions('pages/stock/stockCounting', [
      'fetchStockCountings',
      'resetFilters',
      'setCurrentPage',
      'setPageSize',
      'setFilters',
    ]),
    ...mapActions('pages/stock/stockCounting/stockCountingMaintain', {
      maintainCleanState: 'cleanState',
    }),
    ...mapActions('pages/catalog/products', ['fetchProductCombos']),

    async getData() {
      this.$nextTick(async () => {
        try {
          this.fetching = true
          await this.fetchStockCountings()
          this.queryExecuted = true
        } catch (error) {
          this.showError({ error })
        } finally {
          this.fetching = false
        }
      })
    },

    filterData() {
      this.setCurrentPage(1)
      this.getData()
    },

    resetFiltersLocal() {
      this.resetFilters()
      this.filterData()
    },

    pageSizeChange(pageSize) {
      this.setPageSize(pageSize)
      this.getData()
    },

    pageChange(currentPage) {
      this.setCurrentPage(currentPage)
      this.getData()
    },

    showCreate() {
      this.$refs.sidebar.showSidebar = true
    },

    showEdit(stockCounting) {
      this.maintainCleanState()
      this.$router.push({ name: 'stock-counting-maintain', params: { id: stockCounting.id } })
    },

    showReadOnly(stockCounting) {
      this.maintainCleanState()
      this.$router.push({ name: 'stock-counting-read-only', params: { id: stockCounting.id } })
    },
  },
}
</script>

<style lang="scss" scoped></style>
